import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ShoppingCart, Check, Percent, Euro, Ticket } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

// Flusso backoffice di assistenza all'acquisto:
// compose (scegli corsi/posti) → created (sconto + segna pagato) → fulfilled
// (per le aziende: assegna i posti ai discenti; per i privati: già iscritto).
export default function OrderAssistDialog({ open, onOpenChange, buyer, learners = [], onDone }) {
  const isAzienda = buyer?.kind === "azienda";
  const [step, setStep] = useState("compose");
  const [courses, setCourses] = useState([]);
  const [sel, setSel] = useState({}); // course_id -> seats
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [order, setOrder] = useState(null);
  const [disc, setDisc] = useState({ mode: "pct", value: "", reason: "" });
  const [licenses, setLicenses] = useState([]);

  const reset = useCallback(() => {
    setStep("compose"); setSel({}); setOrder(null);
    setDisc({ mode: "pct", value: "", reason: "" }); setLicenses([]);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    setLoading(true);
    // Admin: elenco completo (anche corsi non a catalogo/bozza) — /courses per un
    // admin restituisce tutti i corsi. La forzatura è consentita solo all'admin.
    api.get("/courses")
      .then((r) => setCourses(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, reset]);

  const toggle = (id) => setSel((s) => {
    const n = { ...s };
    if (n[id]) delete n[id];
    else n[id] = 1;
    return n;
  });
  const setSeats = (id, v) => setSel((s) => ({ ...s, [id]: Math.max(1, parseInt(v || "1", 10) || 1) }));

  const items = Object.entries(sel).map(([course_id, seats]) => ({ course_id, seats: isAzienda ? seats : 1 }));

  const createOrder = async () => {
    if (!items.length) { toast.error("Seleziona almeno un corso."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders", { buyer_user_id: buyer.id, items });
      setOrder(data);
      setStep("created");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const applyDiscount = async () => {
    const val = parseFloat(disc.value);
    if (!val || val <= 0) { toast.error("Inserisci un valore di sconto."); return; }
    if (!disc.reason.trim()) { toast.error("Indica la motivazione dello sconto."); return; }
    setBusy(true);
    try {
      const body = { reason: disc.reason.trim() };
      if (disc.mode === "pct") body.pct = val; else body.amount = val;
      const { data } = await api.post(`/admin/orders/${order.id}/apply-discount`, body);
      setOrder(data);
      toast.success("Sconto applicato.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      let current = order;
      const val = parseFloat(disc.value);
      // Sconto inserito ma non ancora applicato: applicalo prima di pagare, così
      // non si paga a prezzo pieno per dimenticanza (e la provvigione ne tiene conto).
      if (val > 0 && !current.manual_discount) {
        if (!disc.reason.trim()) {
          toast.error("Hai uno sconto non applicato: indica la motivazione o azzera il valore.");
          setBusy(false);
          return;
        }
        const body = { reason: disc.reason.trim() };
        if (disc.mode === "pct") body.pct = val; else body.amount = val;
        const r = await api.post(`/admin/orders/${current.id}/apply-discount`, body);
        current = r.data;
        setOrder(current);
      }
      const { data } = await api.post(`/admin/orders/${current.id}/mark-paid`);
      setOrder(data);
      onDone?.();
      if (isAzienda) {
        const lic = await api.get(`/admin/orders/${current.id}/licenses`);
        setLicenses(lic.data || []);
        setStep("fulfilled");
      } else {
        toast.success("Ordine pagato: il privato è stato iscritto al corso.");
        setStep("fulfilled");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const assignSeat = async (license_id, user_id) => {
    if (!user_id) return;
    setBusy(true);
    try {
      await api.post(`/admin/licenses/${license_id}/assign`, { user_id });
      const lic = await api.get(`/admin/orders/${order.id}/licenses`);
      setLicenses(lic.data || []);
      onDone?.();
      toast.success("Posto assegnato.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const subtotalEstimate = items.reduce((acc, it) => {
    const c = courses.find((x) => x.id === it.course_id);
    return acc + (c ? c.price * it.seats : 0);
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-brand-blue flex items-center gap-2">
            <ShoppingCart size={18} /> Assistenza acquisto
          </DialogTitle>
          <DialogDescription>
            {buyer?.name} · {isAzienda ? "Azienda" : "Privato"}
          </DialogDescription>
        </DialogHeader>

        {step === "compose" && (
          <>
            {loading ? (
              <div className="py-8 text-center text-brand-muted"><Loader2 className="animate-spin inline mr-2" size={18} /> Caricamento corsi…</div>
            ) : (
              <div className="space-y-2 my-2">
                {courses.length === 0 && <p className="text-sm text-brand-muted">Nessun corso disponibile.</p>}
                {courses.map((c) => {
                  const on = !!sel[c.id];
                  const offCatalog = !c.is_published || !c.is_catalog_visible;
                  return (
                    <div key={c.id} className={`flex items-center gap-3 p-2.5 border rounded-sm ${on ? "border-brand-blue bg-brand-blue/5" : "border-brand-line"}`}>
                      <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="h-4 w-4 accent-[#26426B]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-brand-ink truncate flex items-center gap-2">
                          {c.title}
                          {offCatalog && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 shrink-0">
                              {!c.is_published ? "bozza" : "non a catalogo"}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-brand-muted">{eur(c.price)} a posto</div>
                      </div>
                      {on && isAzienda && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-brand-muted">Posti</span>
                          <Input type="number" min="1" value={sel[c.id]} onChange={(e) => setSeats(c.id, e.target.value)} className="h-8 w-16 rounded-sm" />
                        </div>
                      )}
                      {on && !isAzienda && <span className="text-xs text-brand-muted">1 posto</span>}
                    </div>
                  );
                })}
              </div>
            )}
            <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
              <span className="text-sm text-brand-muted">Stima: <b className="text-brand-ink">{eur(subtotalEstimate)}</b></span>
              <Button className="btn-primary" onClick={createOrder} disabled={busy || !items.length}>
                {busy ? <Loader2 className="animate-spin" size={16} /> : "Crea ordine"}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "created" && order && (
          <>
            <div className="my-2 border border-brand-line rounded-sm p-3 text-sm">
              {order.items.map((i) => (
                <div key={i.course_id} className="flex justify-between py-1">
                  <span className="text-brand-ink">{i.course_title} ×{i.seats}</span>
                  <span className="text-brand-muted">{eur(i.line_total)}</span>
                </div>
              ))}
              <div className="border-t border-brand-line mt-2 pt-2 space-y-1">
                <Row k="Subtotale" v={eur(order.subtotal)} />
                {order.manual_discount && <Row k="Sconto" v={"− " + eur(order.subtotal - order.total)} accent />}
                <div className="flex justify-between font-semibold text-brand-ink"><span>Totale</span><span>{eur(order.total)}</span></div>
              </div>
            </div>

            <div className="border border-brand-line rounded-sm p-3 space-y-2">
              <div className="text-xs font-semibold text-brand-muted uppercase tracking-wide">Sconto (facoltativo)</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setDisc({ ...disc, mode: "pct" })} className={`flex-1 text-sm py-1.5 rounded-sm border flex items-center justify-center gap-1 ${disc.mode === "pct" ? "border-brand-blue text-brand-blue bg-brand-blue/5" : "border-brand-line text-brand-muted"}`}><Percent size={14} /> Percentuale</button>
                <button type="button" onClick={() => setDisc({ ...disc, mode: "amount" })} className={`flex-1 text-sm py-1.5 rounded-sm border flex items-center justify-center gap-1 ${disc.mode === "amount" ? "border-brand-blue text-brand-blue bg-brand-blue/5" : "border-brand-line text-brand-muted"}`}><Euro size={14} /> Importo</button>
              </div>
              <div className="flex gap-2">
                <Input type="number" min="0" placeholder={disc.mode === "pct" ? "% sconto" : "€ sconto"} value={disc.value} onChange={(e) => setDisc({ ...disc, value: e.target.value })} className="h-9 rounded-sm w-28" />
                <Input placeholder="Motivazione" value={disc.reason} onChange={(e) => setDisc({ ...disc, reason: e.target.value })} className="h-9 rounded-sm flex-1" />
                <Button variant="outline" className="h-9" onClick={applyDiscount} disabled={busy}>Applica</Button>
              </div>
            </div>

            <DialogFooter className="mt-2">
              <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-brand-muted hover:text-brand-ink">Chiudi (resta in sospeso)</button>
              <Button className="btn-primary inline-flex items-center gap-2" onClick={markPaid} disabled={busy}>
                {busy ? <Loader2 className="animate-spin" size={16} /> : <><Check size={16} /> Segna come pagato</>}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "fulfilled" && (
          <>
            {!isAzienda ? (
              <div className="my-4 text-center">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mb-2"><Check size={24} /></div>
                <p className="text-sm text-brand-ink">Ordine pagato e corso assegnato al privato.</p>
              </div>
            ) : (
              <div className="my-2">
                <div className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-2 flex items-center gap-1.5"><Ticket size={13} /> Assegna i posti ai discenti</div>
                {licenses.length === 0 && <p className="text-sm text-brand-muted">Nessun posto da assegnare.</p>}
                <div className="space-y-2">
                  {licenses.map((lic) => (
                    <div key={lic.id} className="flex items-center gap-2 p-2.5 border border-brand-line rounded-sm">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-brand-ink truncate">{lic.course_title}</div>
                        <div className="text-xs text-brand-muted">{lic.status === "assigned" ? `Assegnato a ${lic.assigned_user_name}` : "Disponibile"}</div>
                      </div>
                      {lic.status === "available" ? (
                        <select
                          className="h-8 rounded-sm border border-brand-line text-sm px-2 max-w-[45%]"
                          defaultValue=""
                          disabled={busy}
                          onChange={(e) => assignSeat(lic.id, e.target.value)}
                        >
                          <option value="" disabled>Scegli discente…</option>
                          {learners.map((l) => (
                            <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-emerald-600"><Check size={16} /></span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button className="btn-primary" onClick={() => onOpenChange(false)}>Fine</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-brand-muted">{k}</span>
      <span className={accent ? "text-brand-gold-deep" : "text-brand-ink"}>{v}</span>
    </div>
  );
}
